import * as React from 'react'
import {useMemo} from 'react'
import tw from 'tailwind-styled-components'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {noPropagate} from '@containers/hooks'
import {cn} from '@containers/html-utils'

const toCss = (value: string | number) => {
  return typeof value === 'number' ? `${value}px` : value
}

export interface DropdownContentProps {
  header?: React.ReactNode
  footer?: React.ReactNode
  width?: string | number
  height?: string | number
  children: React.ReactNode
  className?: string
  disableHeaderMargin?: boolean
  bodyScroll?: boolean
  footerClassName?: string
  headerClassName?: string
  style?: React.CSSProperties
  underHeaderContent?: React.ReactNode
}

const TwHeader = tw.div`
  shrink-0
  bg-header-old
  font-semibold
  text-14
  leading-none
  border-main-old
  border-0
  border-b
  border-solid
  px-8
  flex
  items-center
  justify-between
  h-32
`

const TwFooter = tw.div`
  shrink-0
  border-main-old
  border-0
  border-t
  border-solid
  px-4
  flex
  items-center
  h-32
`

const TwDropdownContent = tw.div`
  flex
  rounded-4
  bg-main-old
  flex-col
  text-main-old
  select-none
  cursor-default
`

/**
 * @description
 * Dropdown content wrapper, used to wrap dropdown content with header and footer
 * @descriptionRu
 * Обертка для содержимого выпадающего списка, используется для оборачивания содержимого выпадающего списка в заголовок и подвал
 * @param props
 * @constructor
 */
export const SharedDropdownContent = (props: DropdownContentProps) => {
  const {
    children,
    style,
    width,
    height,
    bodyScroll,
    header,
    disableHeaderMargin,
    headerClassName,
    footer,
    footerClassName,
    className,
    underHeaderContent
  } = props
  const patchedStyle = useMemo(() => {
    return {
      ...style,
      ...(width && {width: toCss(width)}),
      ...(height && {height: toCss(height)})
    }
  }, [style, width, height])

  const bodyContent = bodyScroll ? (
    <WithPerfectScrollbar
      style={{
        maxHeight: height ? toCss(height) : '100%'
      }}
    >
      {children}
    </WithPerfectScrollbar>
  ) : (
    <div className={cn('flex-1', 'overflow-hidden ')}>{children}</div>
  )

  return (
    <TwDropdownContent
      onClick={noPropagate}
      onMouseDown={noPropagate}
      className={className}
      style={patchedStyle}
    >
      {header && (
        <TwHeader
          className={cn(!disableHeaderMargin && 'mb-4', headerClassName)}
        >
          {header}
        </TwHeader>
      )}
      {underHeaderContent}
      {bodyContent}
      {footer && <TwFooter className={footerClassName}>{footer}</TwFooter>}
    </TwDropdownContent>
  )
}
